<template>
  <div class="review-step" data-testid="review-step">
    <main class="review-step__main">
      <HeadingDisplay
        head="Investir"
        :title="investmentReview.title"
        :subtitle="investmentReview.subtitle"
        class="review-step__head"
      />

      <ContentDisplay content-aspect="outside" class="review-step__content">
        <template #content-block-1>
          <Heading class="review-step__name" :level="6">
            {{ investmentReview.productName }}
          </Heading>
          <ListDisplay
            v-for="(item, index) in investmentReview.productInfo"
            :key="index"
            class="review-step__list-item"
            :title="item.label"
            :value="item.value"
            :show-icon="Boolean(item.details)"
            @click="openDetails(item.details)"
          />
        </template>
      </ContentDisplay>
    </main>
    <DetailsSidebar
      :is-open="isDetailsOpen"
      :data="sidebarDetails"
      @close="isDetailsOpen = false"
    />
    <TermsSignature
      v-if="termSignatureData.requestTermsSignature.length > 0"
      :is-open="isTermSignatureOpen"
      :data="termSignatureData"
      @close="isTermSignatureOpen = false"
      @confirm="acceptTerms"
    />
    <ButtonContainer class="review-step__button-container">
      <template #button-primary>
        <ButtonPrimary
          :loading="isLoading || isFetchingInvestment"
          label="Continuar"
          size="large"
          @click="handleClick"
        />
      </template>
    </ButtonContainer>
  </div>
</template>

<script lang="ts" setup>
import { inject, ref, Ref } from 'vue';

import {
  HeadingDisplay,
  ContentDisplay,
  Heading,
  ListDisplay,
  ButtonContainer,
  ButtonPrimary
} from '@warrenbrasil/nebraska-web';
import { DetailsSidebar } from '@/modules/transaction/components/DetailsSidebar';
import { TermsSignature } from '@/modules/common/components/TermsSignature';

import {
  termSignature,
  IInvestmentReview,
  IDetails,
  ITermSignature
} from '@/modules/transaction/services/product-investment';

import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';

const investmentReview = inject('investmentReview') as Ref<IInvestmentReview>;
const termSignatureData = inject('termSignatureData') as Ref<ITermSignature>;
const isFetchingInvestment = inject('isFetchingInvestment') as Ref<boolean>;

const fetchInvestment = inject('fetchInvestment', () => ({}));

const isLoading = ref(false);
const isTermSignatureOpen = ref(false);
const isDetailsOpen = ref(false);
const sidebarDetails = ref<IDetails>({
  title: '',
  description: '',
  button: null
});

function openDetails(details: IDetails | null) {
  if (!details) return;

  sidebarDetails.value = details;
  isDetailsOpen.value = true;
}

function handleClick() {
  if (termSignatureData.value.requestTermsSignature.length > 0) {
    isTermSignatureOpen.value = true;
    return;
  }

  fetchInvestment();
}

async function acceptTerms() {
  isTermSignatureOpen.value = false;

  try {
    isLoading.value = true;

    await termSignature(
      termSignatureData.value.requestTermsSignature[0].productId
    );
    fetchInvestment();
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Houve um problema ao aceitar os termos. Aguarde alguns instantes e tente novamente.',
      type: NotificationTypes.error
    });
  } finally {
    isLoading.value = false;
  }
}
</script>

<style lang="less" scoped>
.width() {
  max-width: 492px;
  width: 100%;
}

.review-step {
  &__main {
    padding: @size-spacing-x1000 @size-spacing-x500 @size-spacing-x1800;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__head {
    .width();
    margin-bottom: @size-spacing-x800;
  }

  &__content {
    .width();
    background-color: @background-tertiary;
    border: none;
    padding-bottom: @size-spacing-x500;
  }

  &__name {
    margin-bottom: @size-spacing-x500;
  }

  &__list-item {
    margin-bottom: @size-spacing-x350;

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  &__button-container {
    background-color: @background-secondary;
    width: 100%;
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 20;
  }
}
</style>
